import { useEffect, useState } from "react";
import { Info, Plus, Tag, Trash2 } from "lucide-react";
import { useAppServices } from "../../hook/services";
import { cx } from "../../utils";
import Button from "../../components/ui/Button";
import Card from "../../components/ui/Card";
import Input from "../../components/ui/Input";

/**
 * An app's paid plans (Apps.plans): the plan ID the marketplace sends with
 * each install, and the name and price it stands for here.
 *
 * Beside the list: the plan IDs actually seen on this app's installs, so a
 * plan in use can be added with its ID already filled in.
 */

export const INTERVALS = [
  { id: "month", label: "per month" },
  { id: "year", label: "per year" },
  { id: "one_time", label: "one-time" },
];
const CURRENCIES = ["USD", "EUR", "GBP", "CAD", "AUD", "INR", "PKR", "AED"];

export const blankPlan = () => ({ planId: "", name: "", amount: "", currency: "USD", interval: "month", trialDays: "" });

/** Problems the server would refuse, worded for the admin. Empty when fine. */
export const planProblems = (plans) => {
  const out = [];
  const seen = new Set();
  plans.forEach((p, i) => {
    const id = String(p.planId || "").trim();
    const name = String(p.name || "").trim();
    if (!id && !name) return;
    const label = name || id || `Plan ${i + 1}`;
    if (!id) out.push(`"${label}" needs its plan ID from the marketplace.`);
    else if (!/^[A-Za-z0-9_-]+$/.test(id)) out.push(`Plan ID "${id}" can only contain letters, numbers, "-" and "_".`);
    else if (seen.has(id)) out.push(`Plan ID "${id}" is listed twice.`);
    if (id) seen.add(id);
    if (!name) out.push(`Plan ${id || i + 1} needs a name.`);
    const amount = p.amount === "" || p.amount === null || p.amount === undefined ? 0 : Number(p.amount);
    if (!Number.isFinite(amount) || amount < 0) out.push(`The price of "${label}" must be a number of 0 or more.`);
    const trial = p.trialDays === "" || p.trialDays === null || p.trialDays === undefined ? 0 : Number(p.trialDays);
    if (!Number.isInteger(trial) || trial < 0 || trial > 365) out.push(`The trial of "${label}" must be a whole number of days, from 0 to 365.`);
  });
  return out;
};

const fieldCls = "h-9 text-[13px]";
const selectCls =
  "h-9 w-full cursor-pointer rounded-lg border border-ink-200 bg-white px-2 text-[13px] text-ink-800 shadow-sm focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20";

const PlansEditor = ({ appId, plans, onChange }) => {
  const { installs } = useAppServices();
  const [inUse, setInUse] = useState(null);

  useEffect(() => {
    if (!appId) return;
    installs.Plans(appId).then(({ response }) => setInUse(Array.isArray(response) ? response : []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId]);

  const set = (i, key, value) => onChange(plans.map((p, j) => (j === i ? { ...p, [key]: value } : p)));
  const listed = new Set(plans.map((p) => String(p.planId || "").trim()));
  const unnamed = (inUse || []).filter((u) => !listed.has(u.planId));

  return (
    <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_20rem] [&>*]:min-w-0">
      <Card
        title="Plans"
        subtitle="Name and price each plan ID, so installs and analytics show it instead of an ID."
        action={
          <Button type="button" size="sm" variant="secondary" onClick={() => onChange([...plans, blankPlan()])} icon={<Plus size={14} />}>
            Add plan
          </Button>
        }
      >
        {plans.length === 0 ? (
          <div className="rounded-xl border border-dashed border-ink-200 px-6 py-10 text-center">
            <p className="text-sm font-medium text-ink-700">No plans yet</p>
            <p className="mx-auto mt-1 max-w-sm text-[13px] leading-relaxed text-ink-500">
              Add one for each pricing plan on this app's marketplace listing. The plan ID is in the marketplace developer portal, under the app's pricing.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="hidden grid-cols-[minmax(13.5rem,1.5fr)_minmax(0,1fr)_5.5rem_5.5rem_7rem_5.5rem_2rem] gap-2 px-1 text-[11px] font-semibold uppercase tracking-wider text-ink-400 md:grid">
              <span>Plan ID</span>
              <span>Name</span>
              <span>Price</span>
              <span>Currency</span>
              <span>Billed</span>
              <span>Trial</span>
              <span />
            </div>
            {plans.map((p, i) => {
              const count = (inUse || []).find((u) => u.planId === String(p.planId || "").trim());
              return (
                <div key={i} className="rounded-xl border border-ink-200/70 bg-ink-50/40 p-2 md:border-0 md:bg-transparent md:p-0">
                  <div className="grid grid-cols-2 gap-2 md:grid-cols-[minmax(13.5rem,1.5fr)_minmax(0,1fr)_5.5rem_5.5rem_7rem_5.5rem_2rem]">
                    <Input className={cx(fieldCls, "col-span-2 font-mono text-[12px] md:col-span-1")} maxLength={64} value={p.planId} onChange={(e) => set(i, "planId", e.target.value)} placeholder="Plan ID" aria-label="Plan ID" />
                    <Input className={cx(fieldCls, "col-span-2 md:col-span-1")} maxLength={60} value={p.name} onChange={(e) => set(i, "name", e.target.value)} placeholder="e.g. Pro" aria-label="Plan name" />
                    <Input className={cx(fieldCls, "tabular-nums")} type="number" min="0" step="0.01" inputMode="decimal" value={p.amount} onChange={(e) => set(i, "amount", e.target.value)} placeholder="0.00" aria-label="Price" />
                    <select className={selectCls} value={p.currency || "USD"} onChange={(e) => set(i, "currency", e.target.value)} aria-label="Currency">
                      {CURRENCIES.map((c) => (
                        <option key={c}>{c}</option>
                      ))}
                    </select>
                    <select className={selectCls} value={p.interval || "month"} onChange={(e) => set(i, "interval", e.target.value)} aria-label="Billing period">
                      {INTERVALS.map((x) => (
                        <option key={x.id} value={x.id}>
                          {x.label}
                        </option>
                      ))}
                    </select>
                    <div className="relative">
                      <Input className={cx(fieldCls, "pr-10 tabular-nums")} type="number" min="0" max="365" step="1" inputMode="numeric" value={p.trialDays ?? ""} onChange={(e) => set(i, "trialDays", e.target.value)} placeholder="0" aria-label="Trial days" />
                      <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-[11.5px] text-ink-400">days</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => onChange(plans.filter((_, j) => j !== i))}
                      className="grid h-9 w-full place-items-center rounded-lg text-ink-400 hover:bg-rose-50 hover:text-rose-600 md:w-8"
                      aria-label="Remove plan"
                      title="Remove plan"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  {count && (
                    <p className="mt-1 px-1 text-[11.5px] text-ink-400">
                      {count.active} active install{count.active === 1 ? "" : "s"} on this plan
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
        <p className="mt-4 flex items-start gap-2 rounded-lg bg-ink-50 px-3.5 py-3 text-xs leading-relaxed text-ink-500">
          <Info size={14} className="mt-0.5 shrink-0 text-ink-400" />
          Monthly revenue in Analytics counts yearly plans as a twelfth of their price, and leaves one-time plans out.
        </p>
      </Card>

      <aside>
        <Card title="Seen on installs" subtitle="Plan IDs this app's installs came with.">
          {inUse === null ? (
            <p className="text-[12.5px] text-ink-400">{appId ? "Loading…" : "Save the app first."}</p>
          ) : inUse.length === 0 ? (
            <p className="text-[12.5px] text-ink-500">No install has come with a plan yet — free installs don't.</p>
          ) : (
            <ul className="space-y-2">
              {inUse.map((u) => {
                const named = plans.find((p) => String(p.planId || "").trim() === u.planId && String(p.name || "").trim());
                return (
                  <li key={u.planId} className="rounded-lg border border-ink-200/70 bg-white px-3 py-2">
                    <p className="flex items-center gap-1.5 truncate font-mono text-[11.5px] text-ink-600">
                      <Tag size={11} className="shrink-0 text-ink-400" />
                      {u.planId}
                    </p>
                    <div className="mt-1 flex items-center justify-between gap-2">
                      <span className="text-[12px] text-ink-500">
                        {u.active} active{u.total !== u.active ? ` · ${u.total - u.active} uninstalled` : ""}
                      </span>
                      {named ? (
                        <span className="text-[12px] font-medium text-emerald-700">{named.name}</span>
                      ) : listed.has(u.planId) ? (
                        <span className="text-[12px] text-amber-600">Needs a name</span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => onChange([...plans, { ...blankPlan(), planId: u.planId }])}
                          className="inline-flex items-center gap-1 text-[12px] font-medium text-brand-600 hover:underline"
                        >
                          <Plus size={12} /> Add
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          {unnamed.length > 0 && <p className="mt-3 text-[11.5px] text-amber-600">{unnamed.length} plan ID{unnamed.length === 1 ? "" : "s"} in use without a name.</p>}
        </Card>
      </aside>
    </div>
  );
};

export default PlansEditor;
